import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

// components
import { Container, Row, Col } from 'reactstrap';
import Media from 'react-media';
import Feature from 'components/atoms/Feature/';
import Button from 'components/atoms/Button';
import InputField from 'components/atoms/InputField';
import Modal from 'components/atoms/Modal';
import { Icon } from 'components/atoms/Icon/';
import CustomerVehicleHeader from 'components/molecules/CustomerVehicleHeader';
import CustomerVehicleFooter from 'components/molecules/CustomerVehicleFooter';
import CustomerVehicleList from 'components/molecules/CustomerVehicleList';
import SocialLoginContainer from 'components/pages/User/Social/SocialLogin/SocialLoginContainer';
import InvisibleRecaptcha from 'components/molecules/InvisibleRecaptcha';

// actions
import { getCustomerVehicles } from 'store/reducers/customerVehicles/actions';
import { showAlert, disableAlert } from 'store/reducers/alert/actions';
import { goTo } from 'store/reducers/navigation/actions';
import {
  customerVehiclesByField,
  setPhoneNumber,
  setEmail,
  setLicenseNumber
} from 'store/reducers/appointment/actions';
import {
  setAppFlow,
  setNoFindsAlertShown,
  setRecaptchaToken
} from 'store/reducers/userInteractions/actions';
import {
  gtmUserEvent,
  gtmStartGenericEvent,
  gtmRecaptchaEvent
} from 'store/reducers/gtm/actions';

// selectors
import {
  getDealerContact,
  getDealerCountryCode,
  searchCriteriaList,
  isRegistrationNumEnabled,
  isRecaptchaEnabledOnFindMe,
  getRecaptchaToken
} from 'store/reducers/dealerSettings/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { customerVehiclesSelector } from 'store/reducers/customerVehicles/selectors';
import { isAlertEnabled } from 'store/reducers/alert/selectors';
import { getDlrBackgroundImage } from 'store/reducers/dealerData/selectors';
import { getLanguageCode } from 'store/reducers/dealerLocale/selectors';

// utils
import {
  EMAIL_REGEX,
  CHARACTER_EMAIL_REGEX,
  SPECIAL_CHARACTER_EMAIL_REGEX,
  LIMIT_TEN_CHARACTERS
} from 'utils/regex';
import {
  isPhoneNumberValid,
  formatPhoneNumber,
  rawPhoneNumber
} from 'utils/string';

// constants
import { VEHICLE_ROUTE, SIGN_IN_ROUTE } from 'constants/routes';
import { APP_FLOW } from 'constants/appFlow';
import { NAVIGATION } from 'constants/navigations';
import { APP_BREAKPOINTS } from 'constants/appConfig';
import { SEARCH_CRITERIA_FIND_ME } from 'constants/findMe';
import { GTM_API, GTM_RECAPTCHA_EVENTS } from 'constants/recaptcha';

import './StartScreenSearch.scss';
import './Wrapper.scss';

const ns = 'start-screen-search';
const { FIND_ME, IM_NEW_HERE } = APP_FLOW;
const { FORWARD } = NAVIGATION;
const { customerVehicles: customerVehiclesApi } = GTM_API;
let validation;

const errorTypes = {
  SEARCH: 'SEARCH',
  SOCIAL_LOGIN: 'SOCIAL_LOGIN'
};
const { PHONE, EMAIL, LICENSE_NUMBER } = SEARCH_CRITERIA_FIND_ME;

class StartScreenSearch extends Component {
  constructor(props) {
    super(props);
    this.modal = React.createRef();
    this.searchFindMeInput = React.createRef();
    this.modalHeader = React.createRef();
    this.recaptcha = React.createRef();

    this.state = {
      activeRunning: false,
      /**
       * Takes one of the values of `errorTypes` if it's invalid or null if
       * it's valid
       */
      pageErrorType: null,
      inputValue: '',
      rawInputValue: '',
      search: false,
      isListeningForRecaptcha: false
    };
    this.searchHandler = this.searchHandler.bind(this);
    this.formatSearchInput = this.formatSearchInput.bind(this);
    this.closeModalAndGo = this.closeModalAndGo.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onModalClose = this.onModalClose.bind(this);
    this.removeRecaptchaTabIndex = this.removeRecaptchaTabIndex.bind(this);
  }

  componentDidMount() {
    const { disableAlert } = this.props;
    disableAlert();
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.isRecaptchaEnabledOnFindMe === false) {
      delete this.recaptcha;
    }
    return true;
  }

  removeRecaptchaTabIndex() {
    const vehicleModal = document.querySelector('#customer_vehicle_modal');
    if (vehicleModal) {
      const textarea = vehicleModal.querySelector(
        'textarea[name="g-recaptcha-response"]'
      );
      if (textarea) {
        textarea.setAttribute('tabindex', '-1');
      }
    }
  }

  emailValid(email) {
    if (EMAIL_REGEX.test(email)) {
      this.setState({
        pageErrorType: null,
        rawInputValue: email,
        search: true
      });
    }
    return EMAIL_REGEX.test(email);
  }

  phoneNumberValid(phoneNumber) {
    const { dealerContact } = this.props;
    const isValid = isPhoneNumberValid(phoneNumber, dealerContact.country);
    if (isValid) {
      this.setState({ pageErrorType: null });
    }
    return isValid;
  }

  licenseNumberValid(licenseNumber) {
    if (LIMIT_TEN_CHARACTERS.test(licenseNumber)) {
      this.setState({
        pageErrorType: null,
        rawInputValue: licenseNumber,
        search: true
      });
    }
    return LIMIT_TEN_CHARACTERS.test(licenseNumber);
  }

  formatSearchInput(inputValue, searchCriteria) {
    const { dealerContact } = this.props;
    const formattedNumber = formatPhoneNumber(
      inputValue,
      dealerContact.country
    );
    this.setState({
      search: false,
      inputValue
    });
    if (
      searchCriteria.indexOf(PHONE) !== -1 &&
      isPhoneNumberValid(formattedNumber, dealerContact.country)
    ) {
      this.setState({
        rawInputValue: rawPhoneNumber(formattedNumber),
        search: true,
        inputValue: formattedNumber
      });
    } else if (
      searchCriteria.indexOf(EMAIL) !== -1 &&
      (SPECIAL_CHARACTER_EMAIL_REGEX.test(inputValue) ||
        CHARACTER_EMAIL_REGEX.test(inputValue))
    ) {
      this.emailValid(inputValue);
      this.setState({ inputValue });
    } else if (searchCriteria.indexOf(LICENSE_NUMBER) !== -1) {
      this.licenseNumberValid(inputValue);
      this.setState({ inputValue: rawPhoneNumber(formattedNumber) });
    } else {
      this.setState({ inputValue: rawPhoneNumber(formattedNumber) });
    }
  }

  validText(value, searchCriteria) {
    clearTimeout(validation);
    let isInvalid = true;
    let i;
    const validationFn = {
      [PHONE]: 'phoneNumberValid',
      [EMAIL]: 'emailValid',
      [LICENSE_NUMBER]: 'licenseNumberValid'
    };
    for (i = 0; i < searchCriteria.length && isInvalid; i += 1) {
      isInvalid = !this[validationFn[searchCriteria[i]]](value);
    }
    if (value) {
      validation = setTimeout(() => {
        this.setState({
          pageErrorType: isInvalid ? errorTypes.SEARCH : null,
          search: !isInvalid
        });
      }, 3000);
    }
  }

  handleChange = searchCriteria => ({ target: { value } }) => {
    this.setState({
      pageErrorType: null,
      inputValue: value
    });
    if (this.isSearchEnabled()) {
      this.formatSearchInput(value, searchCriteria);
      this.validText(value, searchCriteria);
    }
  };

  fixRawInputValue() {
    const { rawInputValue, inputValue } = this.state;
    if (inputValue && !rawInputValue) {
      const { searchCriteria } = this.searchFindMeInput.current.props;
      this.formatSearchInput(inputValue, searchCriteria);
      this.validText(inputValue, searchCriteria);
    }
  }

  searchHandler = async e => {
    e.preventDefault();
    const {
      pageErrorType,
      inputValue,
      search,
      activeRunning,
      isListeningForRecaptcha
    } = this.state;
    const { isRecaptchaEnabledOnFindMe } = this.props;
    if (!pageErrorType && inputValue !== '' && search && !activeRunning) {
      if (isRecaptchaEnabledOnFindMe) {
        !isListeningForRecaptcha &&
          this.recaptcha.current.initRecaptchaListener();
        this.setState({ isListeningForRecaptcha: true });
        this.recaptcha.current.execute();
      } else {
        await this.doFindMeSearch();
      }
    }
  };

  doFindMeSearch = async () => {
    await this.fixRawInputValue();
    const fieldValue = this.state.rawInputValue;
    const {
      customerVehiclesByField,
      getCustomerVehicles,
      tokenId,
      showAlert,
      goTo,
      setAppFlow,
      gtmUserEvent,
      setNoFindsAlertShown,
      gtmStartGenericEvent,
      recaptchaToken,
      isRecaptchaEnabledOnFindMe,
      gtmRecaptchaEvent
    } = this.props;
    this.setState({
      activeRunning: true
    });
    const fieldName = customerVehiclesByField(fieldValue);
    const success = await getCustomerVehicles(tokenId, fieldName, fieldValue);
    this.modalHeader.current.wrappedInstance.resetTouched();
    this.modal.current.setShowModal(true);
    this.setState({
      activeRunning: false
    });
    setAppFlow(FIND_ME);
    gtmUserEvent(
      'Find Me Button',
      'Clicked',
      'Find Me Selected',
      'GTM_CURRENT_PAGE > Search Section',
      fieldValue
    );
    gtmStartGenericEvent();
    isRecaptchaEnabledOnFindMe &&
      gtmRecaptchaEvent(
        GTM_RECAPTCHA_EVENTS.API_CALL,
        GTM_API.customerVehicles,
        recaptchaToken,
        success
      );
    if (this.props.customerVehicles.length <= 0) {
      this.modal.current.setShowModal(false);
      showAlert();
      setAppFlow(IM_NEW_HERE);
      setNoFindsAlertShown(true);
      goTo(VEHICLE_ROUTE, FORWARD);
    }
  };

  closeModalAndGo = route => {
    this.modal.current.setShowModal(false);
    this.props.goTo(route, FORWARD);
  };

  isSearchEnabled() {
    return Boolean(this.props.dealerContact);
  }

  /**
   * Invoked when a user attempted to do the social login but failed because the
   * user is considered invalid in our system
   */
  onSocialLoginInvalidUser = () => {
    this.setState({ pageErrorType: errorTypes.SOCIAL_LOGIN });
  };

  /**
   * Invoked when a user clicks on one of the social login buttons
   */
  onSocialLoginButtonClick = () => {
    this.setState({ pageErrorType: null });
  };

  buildSearchInputField(placeholder, searchCriteria) {
    const { intl, isRegistrationNumEnabled } = this.props;
    const { activeRunning, pageErrorType, inputValue } = this.state;
    const searchAddonConf = {
      isGlyhicon: true,
      iconClassName: `d-none d-sm-block ${ns}__icon`,
      addonValue: 'search'
    };
    const inputClassNames = classNames({
      [`${ns}__input-field`]: true,
      [`${ns}--input-file-error`]: pageErrorType === errorTypes.SEARCH,
      loading: activeRunning
    });
    return (
      <InputField
        disabled={!this.isSearchEnabled()}
        type={'search'}
        searchCriteria={searchCriteria}
        placeholder={placeholder}
        leftAddonConf={searchAddonConf}
        className={inputClassNames}
        inputConf={{
          name: 'phone_email_search',
          onChange: this.handleChange(searchCriteria),
          value: inputValue
        }}
        ref={this.searchFindMeInput}
        iconAriaHidden={true}
        inputAriaLabel={
          !isRegistrationNumEnabled
            ? intl.formatMessage({
                id: 'c8.aria.enter.phone.or.email.to.start.scheduling.service',
                defaultMessage:
                  'Enter phone or email to start scheduling service'
              })
            : intl.formatMessage({
                id:
                  'c8.aria.enter.phone.email.or.registration.number.to.start.scheduling.service',
                defaultMessage:
                  'Enter phone, email or registration number to start scheduling service'
              })
        }
      />
    );
  }

  onModalClose() {
    const {
      setPhoneNumber,
      setEmail,
      setLicenseNumber,
      setRecaptchaToken,
      isRecaptchaEnabledOnFindMe
    } = this.props;
    if (isRecaptchaEnabledOnFindMe) {
      this.recaptcha.current.reset();
      setRecaptchaToken('');
    }
    setPhoneNumber('');
    setEmail('');
    setLicenseNumber('');
  }

  renderSearchBox() {
    const { intl, searchCriteriaList, isRegistrationNumEnabled } = this.props;
    const { activeRunning } = this.state;
    const buttonClassNames = classNames({
      running: activeRunning
    });
    const getSearchButtonConfig = matches => {
      return (
        <Button
          disabled={!this.isSearchEnabled()}
          className={`d-block ${ns}__search-button`}
          onClick={this.searchHandler}
          id="search_button"
          aria-label={
            isRegistrationNumEnabled
              ? intl.formatMessage({
                  id: 'c8.aria.search.phone.email.registration.number',
                  defaultMessage: 'Search phone, email or registration number'
                })
              : intl.formatMessage({
                  id: 'c8.aria.search.phone.or.email',
                  defaultMessage: 'Search phone or email'
                })
          }
          tabIndex={0}
        >
          <div className={buttonClassNames}>
            {!activeRunning ? (
              matches ? (
                <Icon type="search" />
              ) : (
                intl.formatMessage({
                  id: 'c8.common.search'
                })
              )
            ) : (
              <div className="ld ld-ring ld-spin-fast" />
            )}
          </div>
        </Button>
      );
    };

    return (
      <Col
        className={`d-flex ${ns}__content-form p-0`}
        sm={{ size: 10, offset: 1 }}
        md={{ size: 8, offset: 2 }}
      >
        <form
          className={'flex-grow-1'}
          action="#"
          onSubmit={this.searchHandler}
        >
          <Feature
            name="$phoneEmailSearch"
            render={() =>
              this.buildSearchInputField(
                intl.formatMessage({
                  id: isRegistrationNumEnabled
                    ? 'c8.start.page.start.with.phone.email.or.registration'
                    : 'c8.start.page.start.with.phone.or.email'
                }),
                searchCriteriaList
              )
            }
          />
        </form>
        <Media query={`(max-width: ${APP_BREAKPOINTS.XS - 1}px)`}>
          {matches => getSearchButtonConfig(matches)}
        </Media>
      </Col>
    );
  }

  renderErrorMessage() {
    const { pageErrorType } = this.state;
    return (
      <div className={`${ns}--error-message`} id="error_message">
        {pageErrorType === errorTypes.SEARCH &&
          "Entered info doesn't look right.."}
        {pageErrorType === errorTypes.SOCIAL_LOGIN &&
          'Something went wrong using Facebook'}
      </div>
    );
  }

  render() {
    const {
      dealerContact,
      customerVehicles,
      goTo,
      gtmUserEvent,
      bgUrl,
      intl,
      gtmStartGenericEvent,
      setRecaptchaToken,
      isRecaptchaEnabledOnFindMe,
      gtmRecaptchaEvent,
      languageCode
    } = this.props;
    const modalNS = 'customer-vehicle-modal';
    const { inputValue } = this.state;
    let style = {};
    const url = bgUrl.trim();
    if (url) {
      style = {
        backgroundImage: `url(${url})`
      };
    }
    const searchBoxComputedClassName = classNames(ns, {
      [`${ns}__default-bg`]: !url,
      [`${ns}--with-social-login-buttons`]: this.props.socialButtonsEnabled
    });

    return (
      <Container className="start-screen-search-wrapper">
        {dealerContact && (
          <Modal
            ref={this.modal}
            size={'large'}
            showCloseButton={false}
            className={modalNS}
            onClose={this.onModalClose}
            contentId={`customer_vehicle_modal`}
            header={() => (
              <CustomerVehicleHeader
                onAddonClick={() => {
                  gtmUserEvent(
                    'Back Button',
                    'Clicked',
                    'Back Button Selected',
                    'GTM_CURRENT_PAGE > Schedule Your Service Section > Choose Vehicle',
                    ''
                  );
                  this.modal.current.setShowModal(false);
                  this.onModalClose();
                }}
                inputValue={
                  LIMIT_TEN_CHARACTERS.test(inputValue)
                    ? inputValue.toUpperCase()
                    : inputValue
                }
                goTo={goTo}
                intl={intl}
                ref={this.modalHeader}
              />
            )}
            footer={() => (
              <CustomerVehicleFooter
                onClick={() => this.modal.current.setShowModal(false)}
              />
            )}
            title={() => (
              <Row noGutters>
                <div>
                  {intl.formatMessage({
                    id: 'c8.start.page.modal.choose.vehicle'
                  })}
                </div>
                <div className={`${modalNS}__floating-tag`}>
                  {customerVehicles.length}{' '}
                  {
                    intl
                      .formatMessage({
                        id: 'c8.start.page.modal.vehicles'
                      })
                      .split(' ')[1]
                  }
                </div>
              </Row>
            )}
          >
            <CustomerVehicleList
              carData={customerVehicles}
              goTo={this.closeModalAndGo}
              showMileage={true}
            />
          </Modal>
        )}
        <Row className={searchBoxComputedClassName} style={style}>
          <Col>
            <Row>
              <Col>
                <h1 className={`${ns}__sub-title`} aria-disabled={true}>
                  {intl.formatMessage({
                    id: 'c8.start.page.title.schedule.your.service'
                  })}
                </h1>
              </Col>
            </Row>
            <Col
              tabIndex={0}
              aria-label={intl.formatMessage({
                id: 'c8.aria.returning.customer.sing.in',
                defaultMessage: 'Returning Customer Sign In'
              })}
            >
              <Row>
                <Col className={`px-4`}>{this.renderSearchBox()}</Col>
              </Row>
              <Row>
                <Col>
                  <div
                    className={`d-flex flex-row justify-content-between justify-content-sm-center mt-2`}
                  >
                    <SocialLoginContainer
                      onButtonClick={this.onSocialLoginButtonClick}
                      onInvalidUser={this.onSocialLoginInvalidUser}
                    />
                    <div
                      className={`d-flex flex-column flex-sm-row align-self-center p-sm-3`}
                    >
                      <span
                        className={`${ns}__sign-in-span `}
                        aria-disabled={true}
                      >
                        {intl.formatMessage({
                          id: 'c8.start.page.have.an.account'
                        })}
                      </span>

                      <Feature
                        name="$signInButton"
                        render={() => (
                          <Button
                            className={`${ns}__sign-in-button d-flex justify-content-end`}
                            buttonTheme="link"
                            onClick={() => {
                              gtmStartGenericEvent();
                              setAppFlow(IM_NEW_HERE);
                              goTo(SIGN_IN_ROUTE, FORWARD);
                            }}
                            tabIndex={0}
                            aria-label={intl.formatMessage({
                              id: 'c8.aria.sign.in',
                              defaultMessage: 'Sign in'
                            })}
                          >
                            {intl.formatMessage({
                              id: 'c8.common.sign.in'
                            })}
                          </Button>
                        )}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Col>
        </Row>
        {this.renderErrorMessage()}
        {isRecaptchaEnabledOnFindMe && (
          <InvisibleRecaptcha
            setRecaptchaToken={setRecaptchaToken}
            languageCode={languageCode}
            gtmAction={gtmRecaptchaEvent}
            gtmApi={customerVehiclesApi}
            order={2}
            onResolveAction={async () => {
              this.setState({
                isListeningForRecaptcha: false
              });
              await this.doFindMeSearch();
            }}
            onLoaded={this.removeRecaptchaTabIndex}
            ref={this.recaptcha}
          />
        )}
      </Container>
    );
  }
}

StartScreenSearch.propTypes = {
  bgUrl: PropTypes.string,
  customerVehicles: PropTypes.array,
  customerVehiclesByField: PropTypes.func,
  dealerContact: PropTypes.object,
  dealerCountryCode: PropTypes.string,
  disableAlert: PropTypes.func.isRequired,
  getCustomerVehicles: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired,
  gtmStartGenericEvent: PropTypes.func.isRequired,
  gtmUserEvent: PropTypes.func.isRequired,
  gtmRecaptchaEvent: PropTypes.func.isRequired,
  intl: PropTypes.object,
  isRegistrationNumEnabled: PropTypes.bool.isRequired,
  searchCriteriaList: PropTypes.array.isRequired,
  setAppFlow: PropTypes.func,
  setEmail: PropTypes.func,
  setLicenseNumber: PropTypes.func,
  setPhoneNumber: PropTypes.func,
  showAlert: PropTypes.func.isRequired,
  /** True to display the social buttons (mapped from state) */
  socialButtonsEnabled: PropTypes.bool.isRequired,
  tokenId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setRecaptchaToken: PropTypes.func,
  isRecaptchaEnabledOnFindMe: PropTypes.bool.isRequired,
  languageCode: PropTypes.string
};

function mapStateToProps(state) {
  return {
    tokenId: getTokenId(state),
    customerVehicles: customerVehiclesSelector(state),
    alert: isAlertEnabled(state),
    dealerContact: getDealerContact(state),
    dealerCountryCode: getDealerCountryCode(state),
    bgUrl: getDlrBackgroundImage(state),
    socialButtonsEnabled: true,
    searchCriteriaList: searchCriteriaList(state),
    isRegistrationNumEnabled: isRegistrationNumEnabled(state),
    isRecaptchaEnabledOnFindMe: isRecaptchaEnabledOnFindMe(state),
    languageCode: getLanguageCode(state),
    recaptchaToken: getRecaptchaToken(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      getCustomerVehicles,
      showAlert,
      disableAlert,
      goTo,
      gtmUserEvent,
      customerVehiclesByField,
      setEmail,
      setPhoneNumber,
      setLicenseNumber,
      setAppFlow,
      setNoFindsAlertShown,
      gtmStartGenericEvent,
      gtmRecaptchaEvent,
      setRecaptchaToken
    },
    dispatch
  );
}

export default compose(
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(StartScreenSearch);



// WEBPACK FOOTER //
// ./src/components/organisms/StartScreenSearch/StartScreenSearch.js